import { useEffect } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { ArrowLeft, Phone } from "lucide-react";
import { SERVICES, SERVICE_PRICING_MAP, PHONE_DISPLAY, PHONE_TEL } from "@/data/services";
import { PRICING_GROUPS } from "@/data/pricing";
import { PriceGroupList } from "@/components/site/PriceGroups";

export default function ServicePricing({ onSelectPrice }) {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const service = SERVICES.find((s) => s.id === serviceId);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [serviceId]);

  if (!service) return <Navigate to="/" replace />;

  const isFaste = serviceId === "fasteaftaler";
  const groupIds = SERVICE_PRICING_MAP[serviceId] || [];
  const groups = groupIds
    .map((id) => PRICING_GROUPS.find((g) => g.id === id))
    .filter(Boolean);

  return (
    <section className="py-16 lg:py-20">
      <div className="mx-auto max-w-6xl px-6 lg:px-8">
        <button
          type="button"
          data-testid="back-to-home"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[color:var(--rs-muted)] transition-colors duration-300 hover:text-[color:var(--rs-gold)]"
        >
          <ArrowLeft size={14} /> Tilbage til forsiden
        </button>

        <div className="mt-8 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[color:var(--rs-gold)]">Priser</p>
          <h1
            data-testid="service-pricing-title"
            className="mt-3 font-heading text-4xl tracking-tight text-[color:var(--rs-text)] lg:text-5xl"
          >
            {service.title}
          </h1>
          {!isFaste && (
            <p className="mx-auto mt-3 max-w-2xl text-sm text-[color:var(--rs-muted)]">
              Alle priser inkl. moms — med ekskl. moms nedenunder. Klik en pris for en uforpligtende anmodning.
            </p>
          )}
        </div>

        {isFaste ? (
          <div
            data-testid="faste-aftaler-contact"
            className="mx-auto mt-14 max-w-2xl rounded-2xl border border-[color:var(--rs-border)] bg-[color:var(--rs-panel)] p-8 text-center sm:p-12"
          >
            <h2 className="font-heading text-2xl text-[color:var(--rs-text)]">
              Individuelt tilbud
            </h2>
            <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-[color:var(--rs-muted)]">
              Faste aftaler skræddersys altid til dine behov – uanset om det er fast rengøring,
              havearbejde eller tilbagevendende personale. Kontakt os for et uforpligtende tilbud,
              så finder vi den rigtige løsning til dig.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <button
                type="button"
                data-testid="faste-aftaler-request"
                onClick={() => onSelectPrice(service.title, "Individuelt tilbud", {})}
                className="rounded-full bg-[color:var(--rs-gold)] px-8 py-3 text-sm font-medium uppercase tracking-widest text-[#0A0A0A] transition-colors duration-300 hover:bg-[#F0C74B]"
              >
                Anmod om tilbud
              </button>
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="faste-aftaler-phone"
                className="inline-flex items-center gap-2 rounded-full border border-[color:var(--rs-gold)]/50 px-6 py-3 text-sm tracking-widest text-[color:var(--rs-gold)] transition-colors duration-300 hover:bg-[color:var(--rs-gold)] hover:text-[#0A0A0A]"
              >
                <Phone size={15} /> {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        ) : (
          <div className="mt-14">
            <PriceGroupList groups={groups} onSelectPrice={onSelectPrice} />
          </div>
        )}
      </div>
    </section>
  );
}
